import { useEffect, useMemo, useRef, useState } from 'react';
import { ColorName, Colors, TypographyName } from '../../../theme';
import { Text } from '../../display/Text';
import { Tooltip, TooltipFocusableTrigger } from '../../display/Tooltip';

import NestedThirdRow from './NestedThirdRow';
import PageNavigationBadge from './PageNavigationBadge';
import PageNavigationRow from './PageNavigationRow';
import type { ContentProps, OnSelectType, PageNavigationItem } from './types';
import checkIsTruncated from './utils/checkIsTruncated';

import styles from './NestedSecondRow.module.scss';

export default function NestedSecondRow({
  item,
  onSelect,
}: {
  item: PageNavigationItem;
  onSelect?: OnSelectType;
}) {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsTruncated(checkIsTruncated(textRef));
  }, []);

  const InnerContent = ({ hover, selected }: ContentProps) => {
    const textColor: ColorName =
      hover || selected ? 'UiPrimaryStrongest' : 'GrayWarm10';
    const textType: TypographyName = selected ? 'Body3' : 'Body2';

    return (
      <div className={styles.rowContainer} data-zds>
        <span className={styles.textWrap} data-zds>
          <span
            className={styles.textEllipsis}
            style={{ '--_zds-nestedsecondrow-text-color': Colors[textColor] }}
            ref={textRef}
            data-zds
          >
            <Text color={textColor} margin="0 0 0 8px" type={textType}>
              {item.title}
            </Text>
          </span>
        </span>
        {item.count && (
          <PageNavigationBadge variant="neutral">
            {item.count.toString()}
          </PageNavigationBadge>
        )}
      </div>
    );
  };

  const Content = ({ hover, selected }: ContentProps) =>
    isTruncated ? (
      <Tooltip allowMultiline content={item.title} placement="top right">
        <TooltipFocusableTrigger>
          <InnerContent hover={hover} selected={selected} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ) : (
      <InnerContent hover={hover} selected={selected} />
    );

  const NestedList = useMemo(
    () =>
      item.children
        ? () => (
            <ul className={styles.list} data-zds>
              {item.children?.map((i) => (
                <NestedThirdRow item={i} key={i.id} onSelect={onSelect} />
              ))}
            </ul>
          )
        : undefined,
    [item, onSelect]
  );

  return (
    <PageNavigationRow
      content={Content}
      item={item}
      nestedList={NestedList}
      onSelect={onSelect}
    />
  );
}
